import { useState, type ReactNode } from 'react'
import { RefreshCw } from 'lucide-react'
import { Button, type ButtonProps } from './button'
import { cn } from '@/shared/utils/cn'

interface RefreshButtonProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  /** Acción de refresco. Si devuelve una Promise, el botón muestra estado ocupado. */
  onClick: () => void | Promise<void>
  /** Texto junto al ícono. `null` para solo-ícono. Default "Actualizar". */
  label?: ReactNode
  /** Fuerza el estado ocupado desde fuera (además del await interno). */
  loading?: boolean
}

/**
 * Botón de refresco reutilizable: ícono `RefreshCw` que gira mientras la acción
 * está en curso. Maneja el estado ocupado internamente (await del `onClick`) y
 * además acepta `loading` externo. Variante por defecto `outline`.
 */
export function RefreshButton({
  onClick,
  label = null,
  loading,
  variant = 'outline',
  size = 'icon',
  disabled,
  ...props
}: RefreshButtonProps) {
  const [busy, setBusy] = useState(false)
  const isBusy = busy || !!loading

  const handleClick = async () => {
    if (isBusy) return
    try {
      setBusy(true)
      await onClick()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      onClick={handleClick}
      disabled={disabled || isBusy}
      aria-label={typeof label === 'string' ? label : 'Actualizar'}
      {...props}
    >
      <RefreshCw className={cn('h-4 w-4', isBusy && 'animate-spin')} />
      {label}
    </Button>
  )
}
